<template>
  <!-- Mobile accordion with button -->
  <div class="mb-3 md:mb-4 md:hidden" v-if="show">
    <div
      class="rounded-3xl flex flex-col items-center transition-all duration-200"
      :class="disabled ? 'bg-gray-50 border-gray-200' : 'border border-[#2F3033] bg-white'"
    >
      <button
        @click="toggleOpen"
        :disabled="disabled"
        class="w-full px-4 py-3 flex items-center justify-between transition-colors rounded-3xl"
        :class="[
          disabled ? 'text-gray-400 cursor-not-allowed bg-gray-50' : 'text-gray-700 hover:bg-white'
        ]"
      >
        <div class="flex items-center justify-center">
          <Icon
            name="sliders-vertical"
            class="w-4 h-4 mr-2"
            :class="disabled ? 'text-gray-400' : (hasActiveFilters ? 'text-[#4ECF84]' : 'text-[#2F3033]')"
          />
          <span class="font-semibold text-lg" :class="disabled ? 'text-gray-400' : ''">Refine Trip</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base text-gray-400 font-normal">Optional</span>
          <Icon
            v-if="!disabled"
            :name="open ? 'chevron-up' : 'chevron-down'"
            class="w-5 h-5 text-[#2F3033] transition-transform duration-200"
          />
        </div>
      </button>

      <div v-if="open" class="px-0 pb-2 w-full flex flex-col items-center" style="border-top: 1px solid #e0f7ef;">
        <RefineContentPlan v-if="mode === 'build'" variant="mobile" />
        <RefineContentDiscover v-else variant="mobile" :is-international="isDiscoverInternational" />

        <!-- Clear All Button -->
        <div class="mt-8 pb-6 w-full flex justify-start px-4">
          <button
            @click="clearAll"
            class="w-auto h-[44px] px-6 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center whitespace-nowrap"
          >
            <Trash2 class="w-4 h-4 mr-2" />
            {{ totalSelectedCount }} filters
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop -->
  <div
    v-if="open && !show"
    class="hidden md:flex flex-col mx-auto md:-mt-6 bg-white rounded-3xl shadow-lg border border-gray-200 z-[70]"
    :class="[
      mode === 'build' ? 'max-h-[82vh] min-h-[500px]' : 'max-h-[97vh] min-h-[600px]',
      (isDiscoverLocal || isDiscoverInternational) ? 'md:w-[90%]' : 'md:w-[80%]'
    ]"
  >
    <!-- Header -->
    <div class="relative flex-shrink-0 flex items-center justify-center pt-6 pb-6 px-2 border-b border-gray-200">
      <h3 class="text-base font-semibold text-center">
        {{ isDiscoverInternational ? 'Discover International Trip ideas' : (isDiscoverLocal ? 'Discover Local Trip Ideas' : 'Plan the perfect trip') }}
      </h3>
      <button
        @click="$emit('update:open', false)"
        class="absolute right-6 top-6 text-[#2F3033] hover:text-[#2F3033] transition-colors"
      >
        <X class="w-5 h-5" />
      </button>
    </div>

    <!-- Scrollable content -->
    <div class="overflow-y-auto px-8 pb-6 flex-1 min-h-0">
      <RefineContentPlan v-if="mode === 'build'" variant="desktop" />
      <RefineContentDiscover v-else variant="desktop" :is-international="isDiscoverInternational" />
    </div>

    <!-- Sticky Footer -->
    <div class="pt-2 pb-6 flex gap-10 px-4 border-t border-gray-200 bg-white rounded-b-3xl justify-between">
      <button
        @click="clearAll"
        class="w-auto h-[44px] px-6 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center whitespace-nowrap"
      >
        <Trash2 class="w-4 h-4 mr-2" />
        {{ totalSelectedCount }} filters
      </button>
      <button
        @click="$emit('search')"
        class="flex-1 md:flex-none md:w-64 h-[44px] bg-[#4ECF84] text-white rounded-lg hover:bg-[#45b875] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        :disabled="needsTravellerValidation"
      >
        Search
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useRefineStore } from '@/store/refine.js'
import { X, Trash2 } from 'lucide-vue-next'
import Icon from './Icon.vue'
import RefineContentPlan from './RefineContentPlan.vue'
import RefineContentDiscover from './RefineContentDiscover.vue'

defineOptions({ name: 'TripFinderRefine' })

const props = defineProps({
  show: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
  mode: { type: String, default: 'build' },
  scope: { type: String, default: 'local' }
})

const emit = defineEmits([
  'update:open',
  'search',
  'update:filterCount'
])

const refine = useRefineStore()

// ─── Derived state ────────────────────────────────────────────────────────────

const isDiscoverInternational = computed(() => props.mode === 'recommend' && props.scope === 'international')
const isDiscoverLocal = computed(() => props.mode === 'recommend' && props.scope === 'local')

const open = computed({
  get: () => props.open,
  set: (val) => emit('update:open', val)
})

const hasActiveFilters = computed(() => (
  refine.budgetAmount !== '' ||
  (refine.travelersData.adults > 1 || refine.travelersData.children > 0 || refine.travelersData.pets > 0) ||
  refine.selectedThemes.length > 0 ||
  refine.selectedActivities.length > 0 ||
  refine.selectedTravelMethods.length > 0 ||
  refine.selectedAccommodations.length > 0 ||
  refine.selectedSpecialRequirements.length > 0
))

const totalSelectedCount = computed(() => {
  let count = 0
  if (isDiscoverInternational.value) {
    count += refine.selectedRegions.length
    count += refine.selectedTripTypesDiscover.length
    if (refine.selectedWhoIsGoing) count++
    count += refine.selectedActivities.length
    if (refine.selectedDistanceType) count++
  } else if (isDiscoverLocal.value) {
    count += refine.selectedTripTypesDiscover.length
    if (refine.selectedWhoIsGoing) count++
    count += refine.selectedActivities.length
    if (refine.selectedDistanceType) count++
  } else {
    count += refine.selectedTripTypes.length
    count += refine.selectedActivities.length
    count += refine.selectedThemes.length
    count += refine.selectedTravelMethods.length
    count += refine.selectedAccommodations.length
    count += refine.selectedSpecialRequirements.length
    if (refine.budgetAmount !== '' && refine.budgetAmount > 0) count++
  }
  return count
})

const hasBudget = computed(() => refine.budgetAmount !== '' && refine.budgetAmount > 0)
const hasTravellers = computed(() => refine.travelersData.adults > 0 || refine.travelersData.children > 0)
const needsTravellerValidation = computed(() => hasBudget.value && !hasTravellers.value)

// ─── Actions ──────────────────────────────────────────────────────────────────

const toggleOpen = () => { open.value = !open.value }

const clearAll = () => { refine.clearAll() }

// ─── Watchers ─────────────────────────────────────────────────────────────────

watch(totalSelectedCount, (count) => {
  emit('update:filterCount', count)
}, { immediate: true })

watch([needsTravellerValidation, computed(() => !needsTravellerValidation.value)], ([needsValidation, isValid]) => {
  refine.setValidation({
    needsTravellerValidation: needsValidation,
    isValidForSearch: isValid
  })
}, { immediate: true })
</script>

<style scoped>
.tripfinder-toggle-slider {
  transition: left 0.3s ease-in-out;
}
</style>
